import React, { useState, useMemo, useCallback } from 'react';
import './OzonDelimModal.css';

// ── Known separators ──────────────────────────────────────────────────────────

const INPUT_PRESETS = [
    { label: 'Авто', value: 'auto' },
    { label: ';', value: ';' },
    { label: ',', value: ',' },
    { label: '<br>', value: '<br>' },
    { label: '<br/>', value: '<br/>' },
    { label: '<br/><br/>', value: '<br/><br/>' },
    { label: '↵', value: '\n' },
];

const OUTER_PRESETS = [
    { label: '↵', value: '\n' },
    { label: '<br>', value: '<br>' },
    { label: '<br/>', value: '<br/>' },
    { label: '<br/><br/>', value: '<br/><br/>' },
    { label: '; ', value: '; ' },
    { label: ' | ', value: ' | ' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function detectSep(text) {
    const candidates = ['<br/><br/>', '<br/>', '<br>', ';', ',', '\n'];
    for (const sep of candidates) {
        if (text.split(sep).map(s => s.trim()).filter(Boolean).length > 1) return sep;
    }
    return ' ';
}

function parseKeys(text, inputSep) {
    if (!text) return [];
    const sep = inputSep === 'auto' ? detectSep(text) : inputSep;
    return text.split(sep).map(s => s.trim()).filter(Boolean);
}

function applySimple(keys, outputSep, perLine) {
    if (!keys.length) return '';
    return keys.join(perLine ? '\n' : outputSep);
}

function applyPattern(keys, pattern) {
    if (!keys.length || !pattern.length) return '';
    const parts = [];
    let ki = 0, pi = 0;
    while (ki < keys.length) {
        const row = pattern[pi % pattern.length];
        const n = Math.max(1, parseInt(row.count) || 1);
        const chunk = keys.slice(ki, ki + n);
        ki += n;
        const isLast = ki >= keys.length;
        parts.push(chunk.join(row.innerSep) + (isLast ? '' : row.outerSep));
        pi++;
    }
    return parts.join('');
}

const RANDOM_INNER = [', ', '; ', ' | ', ' '];
const RANDOM_OUTER = ['\n', '\n', '\n', '<br>', '<br/>', '<br/><br/>'];

function randomPattern() {
    const len = 5 + Math.floor(Math.random() * 3); // 5-7 rows
    return Array.from({ length: len }, () => {
        const count = Math.random() < 0.35 ? 1 : 2 + Math.floor(Math.random() * 3); // 35% chance 1 key
        const innerSep = count === 1 ? '' : RANDOM_INNER[Math.floor(Math.random() * RANDOM_INNER.length)];
        const outerSep = RANDOM_OUTER[Math.floor(Math.random() * RANDOM_OUTER.length)];
        return { count: String(count), innerSep, outerSep };
    });
}

// ── SepInput: preset chips + custom text ─────────────────────────────────────

function SepInput({ value, onChange }) {
    const displayVal = value === '\n' ? '\\n' : value;
    const isPreset = OUTER_PRESETS.some(p => p.value === value);
    return (
        <div className="dm-sep-input">
            {OUTER_PRESETS.map(p => (
                <button
                    key={p.value}
                    type="button"
                    className={`dm-chip dm-chip--sm${value === p.value ? ' dm-chip--active' : ''}`}
                    onClick={() => onChange(p.value)}
                >
                    {p.label}
                </button>
            ))}
            <input
                className={`dm-text-input dm-mono dm-text-input--custom${!isPreset ? ' dm-text-input--custom-active' : ''}`}
                value={!isPreset ? displayVal : ''}
                onChange={e => {
                    const v = e.target.value;
                    onChange(v === '\\n' ? '\n' : v);
                }}
                placeholder="свой"
            />
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────

function OzonDelimModal({ products, drafts, selectedOfferIds, onApply, onClose }) {
    const [mode, setMode] = useState('simple');
    const [inputSep, setInputSep] = useState('auto');

    // Simple mode
    const [outputSep, setOutputSep] = useState('\n');
    const [perLine, setPerLine] = useState(false);

    // Pattern mode
    const [pattern, setPattern] = useState([
        { count: '1', innerSep: '', outerSep: '\n' },
        { count: '3', innerSep: ', ', outerSep: '\n' },
    ]);

    // Preview: first selected or first overall
    const previewProduct = useMemo(() => {
        if (selectedOfferIds.size > 0) return products.find(p => selectedOfferIds.has(p.offer_id)) || products[0];
        return products[0];
    }, [products, selectedOfferIds]);

    const previewDesc = useMemo(() => {
        if (!previewProduct) return '';
        return (drafts[previewProduct.offer_id]?.description) ?? (previewProduct.description || '');
    }, [previewProduct, drafts]);

    const previewKeys = useMemo(() => parseKeys(previewDesc, inputSep), [previewDesc, inputSep]);

    const previewResult = useMemo(() => {
        if (mode === 'simple') return applySimple(previewKeys, outputSep, perLine);
        return applyPattern(previewKeys, pattern);
    }, [mode, previewKeys, outputSep, perLine, pattern]);

    const hasSelection = selectedOfferIds.size > 0;

    const handleApply = useCallback(() => {
        if (!hasSelection) return;
        const targets = products.filter(p => selectedOfferIds.has(p.offer_id));
        const results = {};
        for (const product of targets) {
            const desc = (drafts[product.offer_id]?.description) ?? (product.description || '');
            const keys = parseKeys(desc, inputSep);
            if (!keys.length) continue;
            results[product.offer_id] = mode === 'simple'
                ? applySimple(keys, outputSep, perLine)
                : applyPattern(keys, pattern);
        }
        onApply(results);
    }, [hasSelection, products, drafts, selectedOfferIds, inputSep, mode, outputSep, perLine, pattern, onApply]);

    // Pattern row ops
    const addRow = () => setPattern(p => [...p, { count: '1', innerSep: '', outerSep: '\n' }]);
    const removeRow = i => setPattern(p => p.filter((_, idx) => idx !== i));
    const updateRow = (i, field, value) => setPattern(p => p.map((row, idx) => idx === i ? { ...row, [field]: value } : row));

    return (
        <div className="dm-overlay" onClick={onClose}>
            <div className="dm-modal" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="dm-header">
                    <h3 className="dm-title">Переформатировать аннотацию</h3>
                    {hasSelection
                        ? <span className="dm-scope">Выделено: {selectedOfferIds.size}</span>
                        : <span className="dm-scope dm-scope--warn">Выделите товары в таблице</span>
                    }
                </div>

                {/* Input separator */}
                <div className="dm-section">
                    <div className="dm-section-label">Входной разделитель</div>
                    <div className="dm-chips-row">
                        {INPUT_PRESETS.map(p => (
                            <button
                                key={p.value}
                                type="button"
                                className={`dm-chip${inputSep === p.value ? ' dm-chip--active' : ''}`}
                                onClick={() => setInputSep(p.value)}
                            >
                                {p.label}
                            </button>
                        ))}
                        <span className="dm-keys-count">{previewKeys.length} ключей</span>
                    </div>
                </div>

                {/* Mode tabs */}
                <div className="dm-tabs">
                    <button className={`dm-tab${mode === 'simple' ? ' dm-tab--active' : ''}`} onClick={() => setMode('simple')}>Простой</button>
                    <button className={`dm-tab${mode === 'pattern' ? ' dm-tab--active' : ''}`} onClick={() => setMode('pattern')}>Паттерн</button>
                </div>

                {/* ── Simple mode ── */}
                {mode === 'simple' && (
                    <div className="dm-section">
                        <div className="dm-simple-row dm-simple-row--top">
                            <label className="dm-field-label">Заменить разделитель на</label>
                            <SepInput value={outputSep} onChange={setOutputSep} />
                        </div>
                        <label className="dm-perline-check">
                            <input
                                type="checkbox"
                                checked={perLine}
                                onChange={e => setPerLine(e.target.checked)}
                            />
                            Каждый ключ с новой строки
                        </label>
                    </div>
                )}

                {/* ── Pattern mode ── */}
                {mode === 'pattern' && (
                    <div className="dm-section">
                        <div className="dm-pattern-toolbar">
                            <button className="dm-add-row-btn" onClick={addRow}>+ Строка</button>
                            <button className="dm-random-btn" onClick={() => setPattern(randomPattern())}>
                                ⚄ Случайный паттерн
                            </button>
                        </div>
                        <table className="dm-pattern-table">
                            <thead>
                                <tr>
                                    <th className="dm-pt-num">№</th>
                                    <th className="dm-pt-count">Ключей</th>
                                    <th className="dm-pt-inner">Разд. внутри</th>
                                    <th className="dm-pt-outer">Разд. после</th>
                                    <th className="dm-pt-del"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pattern.map((row, i) => (
                                    <tr key={i}>
                                        <td className="dm-pt-num dm-pt-num--body">{i + 1}</td>
                                        <td>
                                            <input
                                                className="dm-text-input dm-text-input--short"
                                                type="number"
                                                min="1"
                                                value={row.count}
                                                onChange={e => updateRow(i, 'count', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                className="dm-text-input dm-mono"
                                                value={row.innerSep}
                                                onChange={e => updateRow(i, 'innerSep', e.target.value)}
                                                placeholder="–"
                                            />
                                        </td>
                                        <td>
                                            <SepInput value={row.outerSep} onChange={v => updateRow(i, 'outerSep', v)} />
                                        </td>
                                        <td>
                                            <button
                                                className="dm-remove-btn"
                                                onClick={() => removeRow(i)}
                                                disabled={pattern.length <= 1}
                                            >✕</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Preview */}
                <div className="dm-section dm-section--preview">
                    <div className="dm-section-label">
                        Предпросмотр
                        {previewProduct && <span className="dm-preview-offer"> — {previewProduct.offer_id}</span>}
                    </div>
                    <div className="dm-preview-wrap">
                        <div className="dm-preview-col">
                            <div className="dm-preview-head">До</div>
                            <pre className="dm-preview-text">{previewDesc || '—'}</pre>
                        </div>
                        <div className="dm-preview-arrow">→</div>
                        <div className="dm-preview-col">
                            <div className="dm-preview-head">После</div>
                            <pre className="dm-preview-text dm-preview-text--after">{previewResult || '—'}</pre>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="dm-footer">
                    <button className="dm-btn" onClick={onClose}>Отмена</button>
                    <button
                        className="dm-btn dm-btn--apply"
                        onClick={handleApply}
                        disabled={!hasSelection}
                        title={!hasSelection ? 'Выделите товары в таблице' : ''}
                    >
                        Применить
                    </button>
                </div>
            </div>
        </div>
    );
}

export default OzonDelimModal;
